"use client";

import { Activity, ArrowRight, Flower2, HeartPulse, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const services = [{
    icon: Leaf,
    title: "Strategic Consulting",
    description: "Expert guidance to navigate complex business challenges with clarity, precision, and actionable insights."
  }, {
    icon: Flower2,
    title: "Growth Optimization",
    description: "Tailored strategies designed to scale your operations and maximize efficiency in a competitive market."
  }, {
    icon: ShieldCheck,
    title: "Risk Management",
    description: "Proactive assessment and mitigation plans to protect your assets and ensure long-term stability."
  }, {
    icon: Activity,
    title: "Performance Analytics",
    description: "Data-driven reporting and performance tracking to help you make informed decisions every day."
  }, {
    icon: Sparkles,
    title: "Digital Transformation",
    description: "Modernizing your infrastructure with cutting-edge technology to keep your business ahead of the curve."
  }, {
    icon: HeartPulse,
    title: "Operational Excellence",
    description: "Streamlining your internal processes to foster a culture of productivity and sustainable success."
  }];
  return <section data-section-id="10068" id="service-offerings-with-pricing" className="bg-background text-foreground py-28 md:py-36 lg:py-44">
      <div className="mx-auto max-w-[1240px] px-5 md:px-6 lg:px-8">
        <div ref={ref} className={`mx-auto max-w-[640px] text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          <h2 className="font-default font-normal text-foreground" style={{
          fontSize: "clamp(2.125rem, 3.4vw + 0.75rem, 3.625rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            Solutions for every <em className="italic font-normal">objective</em>
          </h2>
          <p className="mt-7 font-default text-muted-foreground" style={{
          fontSize: "clamp(1.125rem, 0.4vw + 1rem, 1.3125rem)",
          fontWeight: 400,
          letterSpacing: "-0.003em",
          lineHeight: 1.62
        }}>
            Professional, results-oriented services designed to empower your organization, with solutions crafted to meet your specific goals and operational needs.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <ServiceCard key={i} data-index={i} index={i} Icon={Icon} title={service.title} description={service.description} />;
        })}
        </div>
      </div>
    </section>;
}
type ServiceCardProps = {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  "data-index"?: number;
};
function ServiceCard({
  index,
  Icon,
  title,
  description,
  ...rest
}: ServiceCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`group transition-all ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDuration: "700ms",
    transitionDelay: `${index * 80}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
  }}>
      <Card className="relative h-full bg-card border-0 p-8 gap-0 rounded-lg transition-all ease-out cursor-default" style={{
      transitionDuration: "400ms",
      boxShadow: "0 1px 2px rgba(20,20,30,0.04)"
    }} onMouseEnter={e => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(20,20,30,0.08)";
    }} onMouseLeave={e => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 1px 2px rgba(20,20,30,0.04)";
    }}>
        <div className="flex items-center justify-start">
          <Icon className="size-10 text-primary" strokeWidth={1.5} />
        </div>

        <h3 className="mt-6 font-default text-foreground" style={{
        fontSize: "clamp(1.5rem, 1.4vw + 1rem, 2rem)",
        fontWeight: 600,
        letterSpacing: "-0.012em",
        lineHeight: 1.18
      }}>
          {title}
        </h3>

        <p className="mt-3 font-default text-muted-foreground" style={{
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.7
      }}>
          {description}
        </p>

        <div className="mt-8">
          <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-foreground font-default text-sm relative" style={{
          fontWeight: 500
        }}>
            <span className="relative">
              View details
              <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform ease-[cubic-bezier(0.4,0,0.2,1)]" style={{
              transitionDuration: "320ms"
            }} />
            </span>
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.5} />
          </Link>
        </div>
      </Card>
    </div>;
}